import type { FileCheckIssue, FileCheckLevel } from './print-file-check';

type IssueText = (issue: FileCheckIssue, size: string) => string;

export type FileCheckCopy = {
  heading: string;
  checking: string;
  level: Record<FileCheckLevel, string>;
  sharpAt: (dpi: number, size: string) => string;
  issue: Record<FileCheckIssue['code'], IssueText>;
  fixSmaller: (size: string, dpi: number) => string;
  fixSmallerNote: string;
  fixEditor: string;
  fixUpscale: string;
  fixDpiChecker: string;
  neverBlocks: string;
  cartBadge: Record<Exclude<FileCheckLevel, 'unchecked'>, string>;
  cartFix: string;
};

const NL: FileCheckCopy = {
  heading: 'Bestandscheck',
  checking: 'We controleren je bestand voor het gekozen formaat...',
  level: {
    green: 'Bestand ziet er goed uit',
    orange: 'Bestand is goed bruikbaar, met een tip',
    red: 'Let op: het resultaat kan tegenvallen',
    unchecked: 'Niet automatisch gecontroleerd',
  },
  sharpAt: (dpi, size) => `${dpi} DPI op ${size}: dat wordt scherp.`,
  issue: {
    low_dpi: (issue, size) => issue.severity === 'red'
      ? `Je foto is circa ${issue.dpi} DPI op ${size}. Voor een scherpe print is ${issue.requiredDpi} DPI nodig, dus hij wordt zichtbaar onscherp.`
      : `Je foto is circa ${issue.dpi} DPI op ${size} (advies ${issue.requiredDpi} DPI). Van dichtbij iets zachter, op normale kijkafstand prima.`,
    aspect_mismatch: (issue, size) => `De verhouding van je bestand wijkt af van ${size}. Bij het vullen valt ongeveer ${issue.cropPercent}% aan de randen weg.`,
    orientation_mismatch: () => 'Je bestand is liggend en het formaat staand, of andersom. Draai het ontwerp in de editor of kies de andere richting.',
    no_bleed: () => 'Geen afloop gevonden. Zonder afloop kan bij het snijden een dun wit randje ontstaan.',
    insufficient_bleed: () => 'Te weinig afloop. Bij het snijden kan een dun wit randje ontstaan.',
    size_mismatch: (_issue, size) => `Het eindformaat in je PDF is niet ${size}. Controleer of de uitsnede nog klopt.`,
    flat_dieline: () => 'Voor deze envelop is de opengevouwen werktekening nodig, niet alleen de voorkant.',
    pdf_scaled: () => 'Je PDF heeft een ander formaat met dezelfde verhouding. We schalen hem passend; tekst en vectoren blijven scherp.',
    transparency: () => 'Transparante delen worden wit geprint.',
    color_space: () => 'Je bestand gebruikt een ongebruikelijke kleurruimte. Sla het op als RGB of CMYK voor de beste kleuren.',
    no_cutline: () => 'Geen CutContour-snijlijn gevonden. Voeg die toe als je een snijlijn langs je ontwerp wilt.',
    editor_low_resolution: (issue) => issue.dpi && issue.requiredDpi
      ? `Een foto in je ontwerp is circa ${issue.dpi} DPI (advies ${issue.requiredDpi} DPI) en kan onscherp worden.`
      : 'Een foto in je ontwerp heeft een lage resolutie en kan onscherp worden.',
    not_checked: () => 'Controleer formaat en scherpte in de voorbeeldweergave.',
  },
  fixSmaller: (size, dpi) => `Kies ${size} (${dpi} DPI, scherp)`,
  fixSmallerNote: 'De prijs wordt opnieuw berekend.',
  fixEditor: 'Uitsnede en afloop aanpassen',
  fixUpscale: 'Foto verbeteren met AI',
  fixDpiChecker: 'Bekijk welke formaten je foto aankan',
  neverBlocks: 'Bestellen kan altijd; dit is een advies.',
  cartBadge: {
    green: 'Bestand gecontroleerd',
    orange: 'Bestand gecontroleerd, met een tip',
    red: 'Bestand: kans op een onscherp of afgesneden resultaat',
  },
  cartFix: 'Ontwerp aanpassen',
};

const EN: FileCheckCopy = {
  heading: 'File check',
  checking: 'Checking your file for the chosen size...',
  level: {
    green: 'Your file looks good',
    orange: 'Your file is usable, with one tip',
    red: 'Heads up: the result may disappoint',
    unchecked: 'Not checked automatically',
  },
  sharpAt: (dpi, size) => `${dpi} DPI at ${size}: this will print sharp.`,
  issue: {
    low_dpi: (issue, size) => issue.severity === 'red'
      ? `Your photo is about ${issue.dpi} DPI at ${size}. A sharp print needs ${issue.requiredDpi} DPI, so it will look visibly blurry.`
      : `Your photo is about ${issue.dpi} DPI at ${size} (we advise ${issue.requiredDpi} DPI). Slightly soft up close, fine at normal viewing distance.`,
    aspect_mismatch: (issue, size) => `Your file has a different shape than ${size}. Filling the size crops about ${issue.cropPercent}% off the edges.`,
    orientation_mismatch: () => 'Your file is landscape and the size is portrait, or the other way round. Rotate the design in the editor or choose the other orientation.',
    no_bleed: () => 'No bleed found. Without bleed a thin white edge can appear after cutting.',
    insufficient_bleed: () => 'Not enough bleed. A thin white edge can appear after cutting.',
    size_mismatch: (_issue, size) => `The final size in your PDF is not ${size}. Check that the crop still looks right.`,
    flat_dieline: () => 'This envelope needs the flat, unfolded die-line, not just the front.',
    pdf_scaled: () => 'Your PDF has a different size with the same shape. We scale it to fit; text and vectors stay sharp.',
    transparency: () => 'Transparent areas print as white.',
    color_space: () => 'Your file uses an unusual colour space. Save it as RGB or CMYK for the best colours.',
    no_cutline: () => 'No CutContour cut line found. Add one if you want a cut line around your design.',
    editor_low_resolution: (issue) => issue.dpi && issue.requiredDpi
      ? `A photo in your design is about ${issue.dpi} DPI (we advise ${issue.requiredDpi} DPI) and may print blurry.`
      : 'A photo in your design has a low resolution and may print blurry.',
    not_checked: () => 'Check the size and sharpness in the preview.',
  },
  fixSmaller: (size, dpi) => `Switch to ${size} (${dpi} DPI, sharp)`,
  fixSmallerNote: 'The price is recalculated.',
  fixEditor: 'Adjust crop and bleed',
  fixUpscale: 'Enhance photo with AI',
  fixDpiChecker: 'See which sizes your photo can handle',
  neverBlocks: 'You can always order; this is advice.',
  cartBadge: {
    green: 'File checked',
    orange: 'File checked, with one tip',
    red: 'File: risk of a blurry or cropped result',
  },
  cartFix: 'Edit design',
};

const DE: FileCheckCopy = {
  heading: 'Dateiprüfung',
  checking: 'Wir prüfen Ihre Datei für das gewählte Format...',
  level: {
    green: 'Deine Datei sieht gut aus',
    orange: 'Datei gut verwendbar, mit einem Tipp',
    red: 'Achtung: Das Ergebnis kann enttäuschen',
    unchecked: 'Nicht automatisch geprüft',
  },
  sharpAt: (dpi, size) => `${dpi} DPI bei ${size}: Das wird scharf.`,
  issue: {
    low_dpi: (issue, size) => issue.severity === 'red'
      ? `Ihr Foto hat bei ${size} etwa ${issue.dpi} DPI. Für einen scharfen Druck sind ${issue.requiredDpi} DPI nötig, es wird also sichtbar unscharf.`
      : `Ihr Foto hat bei ${size} etwa ${issue.dpi} DPI (empfohlen ${issue.requiredDpi} DPI). Aus der Nähe etwas weicher, bei normalem Betrachtungsabstand gut.`,
    aspect_mismatch: (issue, size) => `Das Seitenverhältnis Ihrer Datei weicht von ${size} ab. Beim Füllen fallen etwa ${issue.cropPercent}% an den Rändern weg.`,
    orientation_mismatch: () => 'Ihre Datei ist im Querformat und das Format im Hochformat, oder umgekehrt. Drehen Sie das Design im Editor oder wählen Sie die andere Ausrichtung.',
    no_bleed: () => 'Kein Beschnitt gefunden. Ohne Beschnitt kann beim Schneiden ein dünner weißer Rand entstehen.',
    insufficient_bleed: () => 'Zu wenig Beschnitt. Beim Schneiden kann ein dünner weißer Rand entstehen.',
    size_mismatch: (_issue, size) => `Das Endformat in Ihrem PDF ist nicht ${size}. Prüfen Sie, ob der Ausschnitt noch stimmt.`,
    flat_dieline: () => 'Für diesen Umschlag wird die flache, aufgeklappte Stanzvorlage benötigt, nicht nur die Vorderseite.',
    pdf_scaled: () => 'Ihr PDF hat ein anderes Format mit gleichem Seitenverhältnis. Wir skalieren es passend; Text und Vektoren bleiben scharf.',
    transparency: () => 'Transparente Bereiche werden weiß gedruckt.',
    color_space: () => 'Ihre Datei verwendet einen ungewöhnlichen Farbraum. Speichern Sie sie als RGB oder CMYK für die besten Farben.',
    no_cutline: () => 'Keine CutContour-Schneidelinie gefunden. Fügen Sie eine hinzu, wenn Sie eine Schneidelinie um Ihr Design möchten.',
    editor_low_resolution: (issue) => issue.dpi && issue.requiredDpi
      ? `Ein Foto in Ihrem Design hat etwa ${issue.dpi} DPI (empfohlen ${issue.requiredDpi} DPI) und kann unscharf werden.`
      : 'Ein Foto in Ihrem Design hat eine niedrige Auflösung und kann unscharf werden.',
    not_checked: () => 'Prüfen Sie Format und Schärfe in der Vorschau.',
  },
  fixSmaller: (size, dpi) => `${size} wählen (${dpi} DPI, scharf)`,
  fixSmallerNote: 'Der Preis wird neu berechnet.',
  fixEditor: 'Ausschnitt und Beschnitt anpassen',
  fixUpscale: 'Foto mit KI verbessern',
  fixDpiChecker: 'Sehen, welche Formate Ihr Foto schafft',
  neverBlocks: 'Bestellen geht immer; dies ist eine Empfehlung.',
  cartBadge: {
    green: 'Datei geprüft',
    orange: 'Datei geprüft, mit einem Tipp',
    red: 'Datei: Risiko für ein unscharfes oder beschnittenes Ergebnis',
  },
  cartFix: 'Design anpassen',
};

const FR: FileCheckCopy = {
  heading: 'Contrôle du fichier',
  checking: 'Nous vérifions votre fichier pour le format choisi...',
  level: {
    green: 'Votre fichier est en ordre',
    orange: 'Fichier utilisable, avec un conseil',
    red: 'Attention : le résultat risque de décevoir',
    unchecked: 'Non vérifié automatiquement',
  },
  sharpAt: (dpi, size) => `${dpi} DPI en ${size} : l’impression sera nette.`,
  issue: {
    low_dpi: (issue, size) => issue.severity === 'red'
      ? `Votre photo fait environ ${issue.dpi} DPI en ${size}. Une impression nette demande ${issue.requiredDpi} DPI, elle sera donc visiblement floue.`
      : `Votre photo fait environ ${issue.dpi} DPI en ${size} (conseillé ${issue.requiredDpi} DPI). Un peu douce de près, très bien à distance normale.`,
    aspect_mismatch: (issue, size) => `Les proportions de votre fichier diffèrent de ${size}. Pour remplir le format, environ ${issue.cropPercent} % des bords sont coupés.`,
    orientation_mismatch: () => 'Votre fichier est en paysage et le format en portrait, ou l’inverse. Faites pivoter le design dans l’éditeur ou choisissez l’autre orientation.',
    no_bleed: () => 'Aucun fond perdu trouvé. Sans fond perdu, un fin liseré blanc peut apparaître à la découpe.',
    insufficient_bleed: () => 'Fond perdu insuffisant. Un fin liseré blanc peut apparaître à la découpe.',
    size_mismatch: (_issue, size) => `Le format fini de votre PDF n’est pas ${size}. Vérifiez que le cadrage reste correct.`,
    flat_dieline: () => 'Cette enveloppe nécessite le gabarit à plat, déplié, et pas seulement le recto.',
    pdf_scaled: () => 'Votre PDF a un autre format avec les mêmes proportions. Nous l’adaptons ; le texte et les vecteurs restent nets.',
    transparency: () => 'Les zones transparentes sont imprimées en blanc.',
    color_space: () => 'Votre fichier utilise un espace colorimétrique inhabituel. Enregistrez-le en RVB ou CMJN pour de meilleures couleurs.',
    no_cutline: () => 'Aucune ligne de découpe CutContour trouvée. Ajoutez-en une si vous voulez une découpe autour du design.',
    editor_low_resolution: (issue) => issue.dpi && issue.requiredDpi
      ? `Une photo de votre design fait environ ${issue.dpi} DPI (conseillé ${issue.requiredDpi} DPI) et peut être floue.`
      : 'Une photo de votre design a une basse résolution et peut être floue.',
    not_checked: () => 'Vérifiez le format et la netteté dans l’aperçu.',
  },
  fixSmaller: (size, dpi) => `Choisir ${size} (${dpi} DPI, net)`,
  fixSmallerNote: 'Le prix est recalculé.',
  fixEditor: 'Ajuster le cadrage et le fond perdu',
  fixUpscale: 'Améliorer la photo avec l’IA',
  fixDpiChecker: 'Voir les formats possibles pour votre photo',
  neverBlocks: 'Vous pouvez toujours commander ; ceci est un conseil.',
  cartBadge: {
    green: 'Fichier vérifié',
    orange: 'Fichier vérifié, avec un conseil',
    red: 'Fichier : risque de résultat flou ou coupé',
  },
  cartFix: 'Modifier le design',
};

const COPY: Record<string, FileCheckCopy> = { nl: NL, en: EN, de: DE, fr: FR };

/** Andere talen vallen terug op Engels (zie de vier-talen-afspraak). */
export function fileCheckCopy(locale: string | null | undefined): FileCheckCopy {
  const language = String(locale || 'nl').slice(0, 2).toLowerCase();
  return COPY[language] || EN;
}

export function formatSizeCm(target: { widthMm: number; heightMm: number }, locale: string) {
  const format = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  return `${format.format(target.widthMm / 10)} x ${format.format(target.heightMm / 10)} cm`;
}
